import React from 'react';

import { W2_CONTRACT_TID } from '../constants/testids';

interface ContractLinkErrorProps {
  kind: 'expired' | 'invalid' | 'unsupported';
}

const COPY: Record<
  ContractLinkErrorProps['kind'],
  { testid: string; heading: string; body: string }
> = {
  expired: {
    testid: W2_CONTRACT_TID.expired.root,
    heading: 'This signing link has expired',
    body: 'For security, contract signing links only stay active for a limited time. Please ask your OneHealth Plus representative to send a fresh link — your contract details are safe and nothing is lost.',
  },
  invalid: {
    testid: W2_CONTRACT_TID.invalid.root,
    heading: 'This link is invalid',
    body: 'We couldn’t find a contract matching this link. Please double-check the link from your email, or contact your OneHealth Plus representative for a new one.',
  },
  unsupported: {
    testid: W2_CONTRACT_TID.unsupported.root,
    heading: 'We can’t display this contract',
    body: 'This page can’t show your contract in full, so it won’t let you sign something you haven’t been able to read. Please reload the page, and if it keeps happening contact your OneHealth Plus representative — nothing has been signed and nothing is lost.',
  },
};

/**
 * Terminal link states. `expired` renders only when the API itself says the
 * contract is Expired; unknown tokens 404 and render `invalid` — the page never
 * guesses beyond what the API states (§4 hardening: no existence oracle).
 *
 * `unsupported` is the fail-closed state: the server sent a document this build cannot
 * render faithfully, so the page shows no contract and no sign box rather than let someone
 * sign a digest covering content they were never shown.
 */
export const ContractLinkError: React.FC<ContractLinkErrorProps> = ({
  kind,
}) => {
  const copy = COPY[kind];

  return (
    <div data-testid={copy.testid} className="flex flex-col gap-4 py-2">
      <h1 className="text-3xl font-extrabold text-[#1a1a2e] max-sm:text-2xl">
        {copy.heading}
      </h1>
      <p className="text-[15px] leading-6 text-[#5a5a70]">{copy.body}</p>
    </div>
  );
};
